/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';

import { css, cssMap, jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import { Box, Stack } from '@atlaskit/primitives/compiled';
import {
	Card,
	type ElementItem,
	ElementName,
	SmartLinkPosition,
	SmartLinkSize,
	SmartLinkTheme,
	TitleBlock,
} from '@atlaskit/smart-card';
import { token } from '@atlaskit/tokens';

import { isGoogleCalendarUrl } from '../../common/google-calendar/parseGoogleCalendarUrl';
import { GoogleCalendarCard } from '../insight-link-card/google-calendar/GoogleCalendarCard';
import messages from '../insight-link-card/messages';

const FULLSCREEN_CARD_MAX_WIDTH = '336px';

const styles = cssMap({
	grid: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: token('space.100'),
		marginTop: token('space.150'),
	},
	gridFullScreen: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: token('space.100'),
		marginTop: token('space.200'),
	},
	detailListContainer: {
		marginTop: token('space.100'),
		backgroundColor: token('elevation.surface'),
		borderStyle: 'solid',
		borderWidth: token('border.width'),
		borderColor: token('color.border'),
		borderRadius: token('radius.xlarge'),
		overflow: 'hidden',
	},
	detailRow: {
		display: 'block',
		cursor: 'pointer',
	},
	detailRowWithDivider: {
		display: 'block',
		cursor: 'pointer',
		borderTopWidth: token('border.width'),
		borderTopStyle: 'solid',
		borderTopColor: token('color.border'),
	},
	compactCard: {
		display: 'block',
		cursor: 'pointer',
		flex: '0 0 100%',
		minWidth: '0px',
		maxWidth: '100%',
		overflow: 'hidden',
	},
	compactCardFullScreen: {
		display: 'block',
		cursor: 'pointer',
		flex: '0 1 auto',
		minWidth: '0px',
		width: 'fit-content',
		maxWidth: FULLSCREEN_CARD_MAX_WIDTH,
		overflow: 'hidden',
	},
	cardTwoColumn: {
		display: 'block',
		cursor: 'pointer',
		flex: `0 0 calc(50% - ${token('space.050')})`,
		minWidth: '0px',
		maxWidth: '100%',
		overflow: 'hidden',
	},
	detailGridCard: {
		display: 'block',
		cursor: 'pointer',
		flex: `0 0 calc(50% - ${token('space.050')})`,
		minWidth: '0px',
		maxWidth: '100%',
		boxSizing: 'border-box',
		overflow: 'hidden',
		backgroundColor: token('elevation.surface'),
		borderStyle: 'solid',
		borderWidth: token('border.width'),
		borderColor: token('color.border'),
		borderRadius: token('radius.xlarge'),
	},
});

/* eslint-disable @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-important-styles */
// SmartCard internal overrides require nested data-attribute selectors and !important to override inline styles
const smartCardSingleLineTitleOverrides = css({
	'[data-smart-link-container="true"]': {
		minWidth: 0,
	},
	'[data-smart-block="true"]': {
		minWidth: 0,
		maxWidth: '100%',
	},
	'[data-smart-element-group="true"]': {
		minWidth: 0,
		maxWidth: '100%',
		overflow: 'hidden',
	},
	'[data-smart-element-group="true"] span': {
		minWidth: 0,
		maxWidth: '100%',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	'[data-testid="smart-block-unauthorized-view"]': {
		display: 'none',
	},
});

const compactCardStyleOverrides = css({
	'[data-smart-link-container="true"]': {
		marginBlock: token('space.0'),
		marginInline: token('space.0'),
		paddingBlock: token('space.100'),
		paddingInline: token('space.100'),
		maxWidth: '100%',
		overflow: 'hidden',
		borderRadius: token('radius.large'),
		gap: token('space.0'),
		backgroundColor: 'transparent',
		'&:hover': {
			backgroundColor: token('color.background.accent.gray.subtlest.hovered'),
		},
	},
	'[data-smart-block="true"]': {
		columnGap: token('space.100'),
		alignItems: 'center',
		minWidth: 0,
		overflow: 'hidden',
	},
	'[data-smart-element-icon="true"]': {
		width: `${token('space.300')}`,
		height: `${token('space.300')}`,
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		borderStyle: 'solid',
		borderWidth: token('border.width'),
		borderColor: token('color.border'),
		borderRadius: token('radius.medium'),
	},
	'[data-smart-element-icon="true"] > div': {
		width: `${token('space.300')} !important`,
		height: `${token('space.300')} !important`,
	},
	'[data-smart-element="Title"]': {
		color: token('color.text'),
		font: token('font.body.small'),
		fontWeight: token('font.weight.medium'),
		'&:visited': {
			color: token('color.text'),
		},
	},
});

const detailCardStyleOverrides = css({
	'[data-smart-link-container="true"]': {
		marginBlock: token('space.0'),
		marginInline: token('space.0'),
		paddingBlock: token('space.150'),
		paddingInline: token('space.150'),
		maxWidth: '100%',
		overflow: 'hidden',
		borderRadius: '0',
		borderStyle: 'none',
		gap: token('space.0'),
		backgroundColor: 'transparent',
		'&:hover': {
			backgroundColor: token('elevation.surface.hovered'),
		},
	},
	'[data-smart-block="true"]': {
		columnGap: token('space.100'),
		alignItems: 'center',
		minWidth: 0,
		overflow: 'hidden',
	},
	'[data-smart-element-icon="true"]': {
		width: `${token('space.400')}`,
		height: `${token('space.400')}`,
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		borderStyle: 'solid',
		borderWidth: token('border.width'),
		borderColor: token('color.border'),
		borderRadius: token('radius.large'),
	},
	'[data-smart-element-icon="true"] > div': {
		width: `${token('space.400')} !important`,
		height: `${token('space.400')} !important`,
	},
	'[data-smart-element="Title"]': {
		color: token('color.text'),
		font: token('font.heading.xxsmall'),
		'&:visited': {
			color: token('color.text'),
		},
	},
});

const detailCardFullScreenStyleOverrides = css({
	'[data-smart-link-container="true"]': {
		marginBlock: token('space.0'),
		marginInline: token('space.0'),
		paddingBlock: token('space.150'),
		paddingInline: token('space.150'),
		maxWidth: '100%',
		overflow: 'hidden',
		borderRadius: '0',
		borderStyle: 'none',
		gap: token('space.0'),
		backgroundColor: 'transparent',
		'&:hover': {
			backgroundColor: token('elevation.surface.hovered'),
		},
	},
	'[data-smart-block="true"]': {
		columnGap: token('space.150'),
		alignItems: 'center',
		minWidth: 0,
		overflow: 'hidden',
	},
	'[data-smart-element-icon="true"]': {
		width: `${token('space.400')}`,
		height: `${token('space.400')}`,
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		borderStyle: 'solid',
		borderWidth: token('border.width'),
		borderColor: token('color.border'),
		borderRadius: token('radius.large'),
	},
	'[data-smart-element-icon="true"] > div': {
		width: `${token('space.400')} !important`,
		height: `${token('space.400')} !important`,
	},
	'[data-smart-element="Title"]': {
		color: token('color.text'),
		font: token('font.heading.xxsmall'),
		'&:visited': {
			color: token('color.text'),
		},
	},
});

const detailMetadataStyleOverrides = css({
	'[data-smart-element="Provider"]': {
		margin: '0',
		font: token('font.body.small'),
	},
	// Dot on ModifiedOn (not Provider) so it only shows with a date. Symmetric 4px each side.
	'[data-smart-element="ModifiedOn"]::before': {
		content: '•',
		marginBlock: token('space.0'),
		marginInlineStart: token('space.050'),
		marginInlineEnd: token('space.050'),
		font: token('font.body.small'),
	},
	'[data-smart-element="Provider"], [data-smart-element="ModifiedOn"]': {
		color: token('color.text.subtlest'),
		font: token('font.body.small'),
	},
	// Keep the date whole; let the provider truncate first when the row is tight.
	'[data-smart-element="ModifiedOn"]': {
		flexShrink: 0,
	},
	'[data-testid="smart-element-badge-label"]': {
		font: token('font.body.small'),
	},
	'[data-smart-element-group="true"]': {
		gap: token('space.025'),
		// Unitless line-height intentionally collapses Smart Link's inherited metadata line box.
		'line-height': 1,
	},
});
/* eslint-enable @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-important-styles */
const TWO_COLUMN_BREAKPOINT = 480;
const MAX_SINGLE_COLUMN_CARDS = 3;

export type InsightLinkCardVariant = 'compact' | 'detail';

export type InsightLinkCardGroupProps = {
	urls: string[];
	/** Whether the cards are rendered in fullscreen layout. */
	isFullScreen?: boolean;
	/**
	 * Visual variant for the card group.
	 * - `compact` (default): used in the insights feed — 24px icons without border, body.small title, no subtitle, individual card layout.
	 * - `detail`: used in the insight detail view — 32px icons with border, heading.xxs title,
	 * provider subtitle, contiguous stack layout with outer border. Full-screen detail can render
	 * grouped cards in a grid when wide enough.
	 */
	variant?: InsightLinkCardVariant;
	/** Optional callback invoked when a link card is clicked or activated via keyboard. */
	onLinkClick?: (url: string) => void;
};

const CompactCard = memo(
	({
		url,
		isTwoColumn,
		isFullScreen = false,
		variant = 'compact',
		isFirstItem = true,
		onLinkClick,
	}: {
		url: string;
		isTwoColumn: boolean;
		isFullScreen?: boolean;
		variant?: InsightLinkCardVariant;
		isFirstItem?: boolean;
		onLinkClick?: (url: string) => void;
	}): React.ReactElement => {
		const { formatMessage } = useIntl();

		// stopPropagation prevents a duplicate tab when the SmartCard's inner <a> is
		// clicked and the event also bubbles up to the outer clickable Box.
		const openLink = useCallback(
			(event: React.MouseEvent | React.KeyboardEvent) => {
				event.preventDefault();
				event.stopPropagation();
				onLinkClick?.(url);
				window.open(url, '_blank', 'noopener,noreferrer');
			},
			[url, onLinkClick],
		);

		const handleKeyDown = useCallback(
			(event: React.KeyboardEvent<HTMLDivElement>) => {
				if (event.key === 'Enter' || event.key === ' ') {
					openLink(event);
				}
			},
			[openLink],
		);

		const isGoogleCalendarCard = isGoogleCalendarUrl(url);
		const cardContent = isGoogleCalendarCard ? (
			<GoogleCalendarCard
				url={url}
				hideSubtitle={variant === 'compact'}
				titleAppearance={variant === 'detail' ? 'detail' : 'default'}
			/>
		) : variant === 'detail' ? (
			<Card
				appearance="block"
				url={url}
				onClick={openLink}
				ui={{
					theme: SmartLinkTheme.Black,
				}}
			>
				<TitleBlock
					size={SmartLinkSize.Medium}
					position={SmartLinkPosition.Center}
					maxLines={1}
					hideRetry
					subtitle={[
						{ name: ElementName.Provider, hideIcon: true },
						{ name: ElementName.ModifiedOn, hideDatePrefix: true } as ElementItem,
					]}
				/>
			</Card>
		) : (
			<Card
				appearance="block"
				url={url}
				onClick={openLink}
				ui={{
					theme: SmartLinkTheme.Black,
				}}
			>
				<TitleBlock
					size={SmartLinkSize.Medium}
					position={SmartLinkPosition.Center}
					maxLines={1}
					hideRetry
				/>
			</Card>
		);

		return (
			<Box
				role="link"
				aria-label={formatMessage(messages.openLinkInNewTab)}
				tabIndex={0}
				onClick={openLink}
				onKeyDown={handleKeyDown}
				xcss={
					variant === 'detail'
						? isTwoColumn
							? isGoogleCalendarCard
								? styles.cardTwoColumn
								: styles.detailGridCard
							: isFirstItem
								? styles.detailRow
								: styles.detailRowWithDivider
						: isTwoColumn
							? styles.cardTwoColumn
							: isFullScreen
								? styles.compactCardFullScreen
								: styles.compactCard
				}
			>
				{variant === 'detail' ? (
					isFullScreen ? (
						<div
							css={[
								smartCardSingleLineTitleOverrides,
								detailCardFullScreenStyleOverrides,
								detailMetadataStyleOverrides,
							]}
						>
							{cardContent}
						</div>
					) : (
						<div
							css={[
								smartCardSingleLineTitleOverrides,
								detailCardStyleOverrides,
								detailMetadataStyleOverrides,
							]}
						>
							{cardContent}
						</div>
					)
				) : (
					<div css={[smartCardSingleLineTitleOverrides, compactCardStyleOverrides]}>
						{cardContent}
					</div>
				)}
			</Box>
		);
	},
);

const InsightLinkCardGroupBase = ({
	urls,
	isFullScreen = false,
	variant = 'compact',
	onLinkClick,
}: InsightLinkCardGroupProps): React.ReactElement => {
	const containerRef = useRef<HTMLDivElement>(null);
	const [isTwoColumn, setIsTwoColumn] = useState(false);

	useEffect(() => {
		const element = containerRef.current;
		if (!element) {
			return;
		}

		const observer = new ResizeObserver((entries) => {
			for (const entry of entries) {
				setIsTwoColumn(entry.contentRect.width >= TWO_COLUMN_BREAKPOINT);
			}
		});

		observer.observe(element);
		return () => observer.disconnect();
	}, []);

	const showTwoColumnLayout = variant !== 'detail' && isTwoColumn && urls.length > 1;
	const showFullScreenDetailGrid = variant === 'detail' && isFullScreen && isTwoColumn;
	const visibleUrls =
		showTwoColumnLayout || variant === 'detail' ? urls : urls.slice(0, MAX_SINGLE_COLUMN_CARDS);

	// Detail variant: sidebar renders as a contiguous stack; full screen can use separated grid cards.
	if (variant === 'detail') {
		if (visibleUrls.length === 0) {
			return <Box ref={containerRef} xcss={styles.grid} />;
		}
		if (showFullScreenDetailGrid) {
			return (
				<Box ref={containerRef} xcss={styles.gridFullScreen}>
					{visibleUrls.map((url) => (
						<CompactCard
							key={url}
							url={url}
							isTwoColumn
							isFullScreen={isFullScreen}
							variant="detail"
							onLinkClick={onLinkClick}
						/>
					))}
				</Box>
			);
		}
		return (
			<Box ref={containerRef} xcss={styles.detailListContainer}>
				<Stack space="space.0">
					{visibleUrls.map((url, index) => (
						<CompactCard
							key={url}
							url={url}
							isTwoColumn={false}
							isFullScreen={isFullScreen}
							variant="detail"
							isFirstItem={index === 0}
							onLinkClick={onLinkClick}
						/>
					))}
				</Stack>
			</Box>
		);
	}

	// Compact variant: individual cards in a flex grid (like original)
	return (
		<Box ref={containerRef} xcss={isFullScreen ? styles.gridFullScreen : styles.grid}>
			{visibleUrls.map((url) => (
				<CompactCard
					key={url}
					url={url}
					isTwoColumn={showTwoColumnLayout}
					isFullScreen={isFullScreen}
					variant="compact"
					onLinkClick={onLinkClick}
				/>
			))}
		</Box>
	);
};

export const InsightLinkCardGroup: React.MemoExoticComponent<
	(props: InsightLinkCardGroupProps) => React.ReactElement
> = memo(InsightLinkCardGroupBase);
